import type {
  AdjustmentWithCreator,
  CreateAdjustmentDto,
  UpdateAdjustmentDto,
  AdjustmentListQuery,
} from "@repo/share-log-interfaces";
import type { StateStatus, StoreDeps } from "../types";
import type { ImmerSet } from "../helpers/create-store";
import { createStore } from "../helpers/create-store";
import { withFetchStatus } from "../helpers/with-fetch-status";
import { createReset } from "../helpers/create-reset";

const DEFAULT_LIMIT = 20;

interface AdjustmentState {
  adjustments: AdjustmentWithCreator[];
  currentAdjustment: AdjustmentWithCreator | null;
  total: number;
  page: number;
  limit: number;
  hasMore: boolean;
  status: StateStatus;
  detailStatus: StateStatus;
  filter: AdjustmentListQuery;
}

interface AdjustmentActions {
  fetchAdjustments: (
    workspaceId: number,
    query?: AdjustmentListQuery,
  ) => Promise<void>;
  loadMore: (workspaceId: number) => Promise<void>;
  fetchAdjustment: (
    workspaceId: number,
    adjustmentId: number,
  ) => Promise<void>;
  createAdjustment: (
    workspaceId: number,
    dto: CreateAdjustmentDto,
  ) => Promise<AdjustmentWithCreator>;
  updateAdjustment: (
    workspaceId: number,
    adjustmentId: number,
    dto: UpdateAdjustmentDto,
  ) => Promise<AdjustmentWithCreator>;
  deleteAdjustment: (
    workspaceId: number,
    adjustmentId: number,
  ) => Promise<void>;
  completeAdjustment: (
    workspaceId: number,
    adjustmentId: number,
  ) => Promise<AdjustmentWithCreator>;
  setFilter: (filter: AdjustmentListQuery) => void;
  reset: () => void;
}

export type AdjustmentStore = AdjustmentState & AdjustmentActions;

const initialState: AdjustmentState = {
  adjustments: [],
  currentAdjustment: null,
  total: 0,
  page: 1,
  limit: DEFAULT_LIMIT,
  hasMore: false,
  status: "idle",
  detailStatus: "idle",
  filter: {},
};

export function createAdjustmentStore(deps: StoreDeps) {
  return createStore<AdjustmentStore>("AdjustmentStore", (set, get) => ({
    ...initialState,

    fetchAdjustments: (workspaceId, query?) =>
      withFetchStatus(
        (s) => set((state) => { state.status = s; }),
        deps.logger,
        async () => {
          set((state) => { state.filter = query ?? {}; });
          const result = await deps.api.adjustment.list(workspaceId, {
            ...query,
            page: 1,
            limit: DEFAULT_LIMIT,
          });
          set((state) => {
            state.adjustments = result.adjustments;
            state.total = result.total;
            state.page = result.page;
            state.limit = result.limit;
            state.hasMore = result.hasMore;
          });
        },
      ),

    loadMore: async (workspaceId) => {
      const { hasMore, status, page, filter } = get();
      if (!hasMore || status === "loading") return;

      await withFetchStatus(
        (s) => set((state) => { state.status = s; }),
        deps.logger,
        async () => {
          const nextPage = page + 1;
          const result = await deps.api.adjustment.list(workspaceId, {
            ...filter,
            page: nextPage,
            limit: DEFAULT_LIMIT,
          });
          set((state) => {
            state.adjustments = [...state.adjustments, ...result.adjustments];
            state.total = result.total;
            state.page = result.page;
            state.limit = result.limit;
            state.hasMore = result.hasMore;
          });
        },
      );
    },

    fetchAdjustment: (workspaceId, adjustmentId) =>
      withFetchStatus(
        (s) => set((state) => { state.detailStatus = s; }),
        deps.logger,
        async () => {
          const adjustment = await deps.api.adjustment.get(
            workspaceId,
            adjustmentId,
          );
          set((state) => { state.currentAdjustment = adjustment; });
        },
      ),

    createAdjustment: async (workspaceId, dto) => {
      const adjustment = await deps.api.adjustment.create(workspaceId, dto);
      set((state) => {
        state.adjustments.unshift(adjustment);
        state.total += 1;
      });
      return adjustment;
    },

    updateAdjustment: async (workspaceId, adjustmentId, dto) => {
      const adjustment = await deps.api.adjustment.update(
        workspaceId,
        adjustmentId,
        dto,
      );
      set((state) => {
        const idx = state.adjustments.findIndex((a) => a.id === adjustmentId);
        if (idx !== -1) state.adjustments[idx] = adjustment;
        if (state.currentAdjustment?.id === adjustmentId) {
          state.currentAdjustment = adjustment;
        }
      });
      return adjustment;
    },

    deleteAdjustment: async (workspaceId, adjustmentId) => {
      await deps.api.adjustment.remove(workspaceId, adjustmentId);
      set((state) => {
        state.adjustments = state.adjustments.filter(
          (a) => a.id !== adjustmentId,
        );
        state.total -= 1;
        if (state.currentAdjustment?.id === adjustmentId) {
          state.currentAdjustment = null;
        }
      });
    },

    completeAdjustment: async (workspaceId, adjustmentId) => {
      const adjustment = await deps.api.adjustment.complete(
        workspaceId,
        adjustmentId,
      );
      set((state) => {
        const idx = state.adjustments.findIndex((a) => a.id === adjustmentId);
        if (idx !== -1) state.adjustments[idx] = adjustment;
        if (state.currentAdjustment?.id === adjustmentId) {
          state.currentAdjustment = adjustment;
        }
      });
      return adjustment;
    },

    setFilter: (filter) => {
      set((state) => { state.filter = filter; });
    },

    reset: createReset(set as ImmerSet<AdjustmentState>, initialState),
  }));
}
